import { lngLatToMercator }        from '@tokamap/core';
import { pointInPolygon }          from '@tokamap/core';
import { DEFAULT_NEW_SHAPE_LAYER } from '@tokamap/core';
import type { ShapeStyle }         from '@tokamap/core';
import type { LngLat }             from '@tokamap/core';
import { generateId }              from '@tokamap/core';
import type { BasemapBuilding }    from '@tokamap/core';
import type { LatestRawFeature }   from '@tokamap/core';
import type { TokaMapInstance }    from '@tokamap/core';
import { BACKGROUND_ID_PREFIX }    from '@/TokamapEditor/helpers/planBackground';
import { SCAFFOLD_OPTIONS }        from '@/TokamapEditor/helpers/planBackground';
import { PLAN_SHAPE_STYLES }       from '@/TokamapEditor/helpers/planShapes';

/**
 * **Le bâti du fond OSM, repris en formes de la carte** — l'autre moitié du lot 6 de
 * `services/map/docs/PLAN_ANALYSE_ZONE_CARTE.md`, celle qui vit dans l'éditeur.
 *
 * `@tokamap/core` (`queryBasemapBuildings`) lit les polygones du fond vectoriel ; ce module les
 * pose. Deux gestes distincts, et c'est tout l'intérêt :
 *
 * 1. **l'aperçu** — des échafaudages verrouillés, jamais enregistrés, qui montrent ce qui SERAIT
 *    créé. C'est l'étape de tri : l'opérateur voit le compte et le contour avant de décider ;
 * 2. **la création** — de vraies formes, qui suivent le chemin ordinaire de l'éditeur
 *    (`addShapeFromFeature` sans `skipSave` -> `onSave` -> `FeatureModel`). Aucune API d'import
 *    n'est appelée : il n'y a ni fichier, ni analyse, ni placement à déduire — les coordonnées
 *    SONT déjà celles de la carte.
 *
 * ## Pourquoi ça ne passe pas par l'écran de vérification d'un import
 *
 * Cet écran travaille sur le résultat d'une analyse SERVEUR : il trie des formes détectées dans un
 * document, par calque et par doute. Un bâtiment OSM n'a ni calque, ni doute, ni document — il n'y
 * a rien à y arbitrer, seulement à regarder puis accepter. Lui fabriquer un import pour traverser
 * cet écran demanderait au serveur d'accepter des géométries toutes faites, c'est-à-dire un second
 * chemin de génération pour ne rien décider de plus.
 */

/**
 * Préfixe des formes d'APERÇU.
 *
 * Il porte le préfixe commun aux échafaudages d'import ({@link BACKGROUND_ID_PREFIX}) : c'est lui
 * qui garantit qu'aucun chemin — y compris un geste de l'opérateur sur la carte — ne les
 * enregistrera comme des Features (voir le `onSave` de `TokamapEditor/index.tsx`). Les formes
 * CRÉÉES, elles, ne le portent surtout pas : ce sont des formes de la carte comme les autres.
 */
export const BUILDING_PREVIEW_PREFIX = `${BACKGROUND_ID_PREFIX}osm-building-`;

/** Id d'aperçu d'un bâtiment, stable pour un rang donné. */
export function buildingPreviewShapeId(index: number): string {
	return `${BUILDING_PREVIEW_PREFIX}${index}`;
}

/** Vrai pour une forme d'aperçu de bâtiment — voir {@link BUILDING_PREVIEW_PREFIX}. */
export function isBuildingPreviewShapeId(shapeId: string): boolean {
	return shapeId.startsWith(BUILDING_PREVIEW_PREFIX);
}

/**
 * Feature d'éditeur (format v2, coordonnées 3857) d'un bâtiment.
 *
 * Les anneaux arrivent en lng/lat, ouverts (convention de `BasemapBuilding`), cours comprises : on les projette et on les
 * ferme, le format attendant le premier point répété en dernier.
 *
 * L'APERÇU est verrouillé (`extra.lock`) et peint de la table de l'écran de tri
 * ({@link PLAN_SHAPE_STYLES}) — la même table, pour que deux écrans qui disent la même chose ne
 * puissent pas la dire de deux couleurs : vert « sera créé », gris « écarté ». La forme CRÉÉE ne
 * porte aucun style : elle prend celui de la carte, comme une forme dessinée à la main.
 *
 * Le verrou n'est pas qu'une protection, c'est ce qui REND le tri possible : une forme verrouillée
 * n'émet pas `shape:click` et le clic retombe en `map:click` (voir `handleShapeClick` dans
 * `MapLibreAdapter`). L'éditeur ne sélectionne donc rien pendant qu'on trie, et le panneau retrouve
 * lui-même le bâtiment visé ({@link buildingAtPoint}). C'est ce qui manquait à l'aperçu d'import,
 * où le décochage au clic et la sélection de l'éditeur se disputaient le même geste.
 */
export function buildBuildingFeature(params: {
	shapeId: string;
	building: BasemapBuilding;
	preview: boolean;
	/** Aperçu seulement : `false` peint le gris « écarté ». */
	kept?: boolean;
	/** Création seulement : le style par défaut de la carte, celui d'une forme dessinée à la main. */
	style?: ShapeStyle;
	/** Création seulement : le thème par défaut de la carte, comme pour une forme dessinée à la main. */
	themeId?: string;
}): LatestRawFeature {
	const { shapeId, building, preview, kept = true, style, themeId } = params;

	const closed = (ring: LngLat[]) => [...ring, ring[0]!].map(point => lngLatToMercator(point));

	return {
		type: 'Feature',
		geometry: {
			type: 'Polygon',
			coordinates: [building.ring, ...(building.holes ?? [])].map(closed),
			properties: {
				id: shapeId,
				extra: preview
					? {
						// L'aperçu ne doit pas bloquer le calcul d'itinéraire le temps qu'il est posé.
						walkable: true,
						// Il se regarde : ni sélection, ni édition, ni déplacement accidentel.
						lock: true,
						style: PLAN_SHAPE_STYLES[kept ? 'enabled' : 'discarded'],
					}
					// Une forme créée porte le style par défaut de la CARTE, comme une forme dessinée à
					// la main — surtout pas le vert de l'aperçu, qui ne disait qu'un état de tri.
					: { ...(style ? { style } : {}), ...buildingVolume(building) },
				...(!preview && themeId ? { theme: themeId } : {}),
			},
		},
	};
}

function buildingVolume({ height, minHeight = 0 }: BasemapBuilding): { height?: number; elevation?: number; } {
	if (!height || height <= minHeight) return {};

	return { height: height - minHeight, ...(minHeight > 0 ? { elevation: minHeight } : {}) };
}

/**
 * Pose l'aperçu des bâtiments, en remplaçant celui qui traînerait.
 *
 * Rend les ids posés — de quoi les compter, et rien de plus : c'est {@link removeBuildingsPreview}
 * qui les retire, en les retrouvant par leur préfixe plutôt qu'en retenant une liste que le
 * moindre rechargement ferait mentir.
 */
export function previewBuildings(instance: TokaMapInstance, buildings: BasemapBuilding[]): string[] {
	const features = buildings.map((building, index) => (
		buildBuildingFeature({ shapeId: buildingPreviewShapeId(index), building, preview: true })
	));

	let ids: string[] = [];
	instance.shapeStore.batch(() => {
		removeBuildingsPreview(instance);
		ids = instance.addShapesFromFeatures(features, SCAFFOLD_OPTIONS);
	});

	return ids;
}

/**
 * Le bâtiment sous un point de la carte, ou `null`.
 *
 * Le PLUS PETIT des bâtiments qui contiennent le point l'emporte : dans une cour d'immeuble, une
 * petite annexe est souvent posée sur l'emprise d'un grand bâtiment, et c'est toujours la petite
 * qu'on vise en cliquant dessus — la grande reste atteignable partout ailleurs.
 */
export function buildingAtPoint(buildings: BasemapBuilding[], point: LngLat): number | null {
	let best: number | null = null;
	let bestArea = Infinity;

	buildings.forEach((building, index) => {
		if (!pointInPolygon(point, building.ring) || building.holes?.some(hole => pointInPolygon(point, hole))) return;

		const area = ringArea(building.ring);
		if (area < bestArea) {
			best = index;
			bestArea = area;
		}
	});

	return best;
}

/** Aire (lacet de Gauss), en degrés² : sans unité utile, elle ne sert qu'à COMPARER deux anneaux. */
function ringArea(ring: LngLat[]): number {
	let sum = 0;
	for (let i = 0; i < ring.length; i++) {
		const [x0, y0] = ring[i]!;
		const [x1, y1] = ring[(i + 1) % ring.length]!;
		sum += x0 * y1 - x1 * y0;
	}

	return Math.abs(sum) / 2;
}

/** Retire tout aperçu de bâtiment posé sur la carte. */
export function removeBuildingsPreview(instance: TokaMapInstance): void {
	instance.shapeStore.batch(() => {
		instance.shapeStore
			.getAll()
			.filter(shape => isBuildingPreviewShapeId(shape.id))
			.forEach(shape => instance.deleteShape(shape.id, SCAFFOLD_OPTIONS));
	});
}

/**
 * Crée les bâtiments comme formes de la carte, et retire l'aperçu.
 *
 * Chaque forme prend un id NEUF ({@link generateId}, celui de l'éditeur) : réutiliser l'id
 * d'aperçu la rendrait indiscernable d'un échafaudage, et le `onSave` refuserait précisément de
 * l'enregistrer. Sans `skipSave`, chaque création suit le chemin ordinaire et devient une Feature.
 *
 * Rend les ids créés.
 */
export function createBuildingShapes(instance: TokaMapInstance, buildings: BasemapBuilding[]): string[] {
	removeBuildingsPreview(instance);

	// Exactement ce que l'adaptateur donne à une forme DESSINÉE : sa bande de z-order et le style
	// par défaut de la carte. Le calque n'est pas cosmétique — `onSave` l'envoie en
	// `layer: shape.layer?.toString()`, et une forme sans calque part en merge-patch SANS la clé,
	// que l'API refuse (« layer: Cette valeur ne doit pas être vide », 422 sur chaque bâtiment).
	const style = instance.getDefaultShapeStyle();
	const themeId = instance.getDefaultThemeId();
	const features = buildings.map(building => (
		buildBuildingFeature({ shapeId: generateId(), building, preview: false, ...(style ? { style } : {}), ...(themeId ? { themeId } : {}) })
	));

	// Un seul lot : une entrée d'historique, un rendu, et un seul `onSave` — donc une requête.
	return instance.addShapesFromFeatures(features, { layer: DEFAULT_NEW_SHAPE_LAYER });
}
